import { StyleSheet } from "react-native-unistyles";
import { Banner } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";
import { useTenantActivations } from "@visioni/a3s-monitoring/app";
import { patriotToolsHref } from "../routes";
import { useTenantListFilters } from "../hooks/useTenantListFilters";
import { ActivationFilterBar } from "../components/ActivationFilterBar";
import { ActivationsTable } from "../components/ActivationsTable";
import { QueryNotice } from "../components/QueryNotice";

/**
 * Filters live in the URL (`?site=&camera=&tz=&from=&to=`) so a filtered list can be
 * shared. The page does not: a cursor is an opaque replica key, and a shared
 * link should open on the newest activations.
 */
export function TenantActivationsScreen() {
  const { tenantId, values, scope, timeZone, fallback, parsed, onChange } = useTenantListFilters();
  const filters = parsed?.ok === true ? parsed.filters : {};
  const list = useTenantActivations({ filters, enabled: parsed?.ok === true });

  return (
    <View style={styles.page}>
      <View style={styles.filterRow}>
        {timeZone !== undefined && (
          <ActivationFilterBar
            values={{ ...values, tz: timeZone }}
            onChange={onChange}
            sites={scope.sites}
            cameras={scope.cameras}
            isLoading={scope.isLoading}
            timeZone={timeZone}
            fallback={fallback}
            offsetsAt={filters.since === undefined ? new Date() : new Date(filters.since)}
          />
        )}
      </View>
      {parsed === undefined || timeZone === undefined ?
        <View style={styles.notice}>
          <QueryNotice isLoading error={null} onRetry={list.refetch} />
        </View>
      : !parsed.ok ?
        <View style={styles.notice}>
          <Banner variant="warning" title="Filters not applied" message={parsed.problem} />
        </View>
      : list.items === undefined || list.error !== null ?
        <View style={styles.notice}>
          <QueryNotice isLoading={list.isLoading} error={list.error} onRetry={list.refetch} />
        </View>
      : list.items.length === 0 && !list.pager.canGoBack ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            No activations found
          </Text>
        </View>
      : <ActivationsTable
          items={list.items}
          isStale={list.isPlaceholderData}
          isFetching={list.isFetching}
          hrefFor={(activation) =>
            patriotToolsHref(tenantId, `tenant-activations/${encodeURIComponent(activation.ticketNo)}`)
          }
          siteLabel={scope.siteLabel}
          cameraLabel={scope.cameraLabel}
          timeZone={timeZone}
          pager={list.pager}
          nextCursor={list.nextCursor}
        />
      }
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  page: { flex: 1 },
  filterRow: {
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    // Above the list, so the filter dropdowns open over the rows.
    zIndex: 1,
  },
  notice: { paddingHorizontal: theme.spacing.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", padding: theme.spacing["4xl"] },
}));
